import { z } from "zod"
import { SortDirection } from "@tanstack/react-table"

/**
 * Herbruikbaar Zod schema voor table search parameters.
 * Gebruik dit in je route definitie voor consistente validatie.
 *
 * @example
 * ```typescript
 * import { createTableSearchSchema } from "toptaal-shared"
 *
 * export const Route = createFileRoute("/jouw-route/")({
 *   validateSearch: createTableSearchSchema(),
 * })
 * ```
 *
 * @example Met custom default waarden
 * ```typescript
 * export const Route = createFileRoute("/jouw-route/")({
 *   validateSearch: createTableSearchSchema({
 *     defaultSortField: "name",
 *     defaultSortDirection: "desc",
 *   }),
 * })
 * ```
 */
export function createTableSearchSchema(options?: {
  defaultSortField?: string
  defaultSortDirection?: SortDirection
  defaultPage?: number
}) {
  return z.object({
    page: z
      .number()
      .optional()
      .catch(options?.defaultPage ?? 1),
    search: z.string().optional().catch(""),
    sortField: z
      .string()
      .optional()
      .catch(options?.defaultSortField ?? "id"),
    sortDirection: z
      .enum(["asc", "desc"])
      .optional()
      .catch(options?.defaultSortDirection ?? "asc"),
    columnVisibility: z.string().optional(),
  })
}

/**
 * Type voor table search parameters.
 * Gebruik dit als type hint voor je search params.
 */
export type TableSearchParamsSchema = z.infer<
  ReturnType<typeof createTableSearchSchema>
>
